import dynamic from "next/dynamic";
import Head from "next/head";
import Image from "next/image";
import { useRouter } from "next/router";

import { useEffect, useMemo, useState } from "react";

import { twMerge } from "tailwind-merge";

import AccountUpgradeForm from "components/AccountUpgradeForm";
import CustomCard from "components/CustomCard";
import { Loader } from "components/customElements/loader";
import { entityUrl } from "components/EntityPageLayout";
import SignUpForm from "components/SignUpForm";
import { Tab, Tabs } from "components/Tabs";
import { useApi } from "hooks/useApi";
import useAuthentication from "hooks/useAuthentication";
import { useScrollable } from "hooks/useScrollable";
import { baseFetcher } from "utils/baseFetcher";
import shortSecurity from "utils/shortSecurity";
import logo from "consts/logo";

import EntityPageEstimatesCombined from "../EntityPageEstimates/combined";

import ESG from "./ESG";
import Financials from "./Financials";
import Liquidity from "./Liquidity";
import Profitability from "./Profitability";
import Segments from "./Segments";
import Shareholder, { shareholdersUrl } from "./Shareholder";
import { TimeScopeProvider } from "./ToggleTimeScope";
import useFinancialData from "./useFinancialData";
import Valuation from "./Valuation";
import WorkingCapital from "./WorkingCapital";

const Snapshot = dynamic(() => import("./Snapshot"), {
  ssr: false,
});

const PeersComparison = dynamic(() => import("./PeersComparison"), {
  ssr: false,
});

type EntityPageDataProps = { slug?: string; isModal?: boolean };

const EntityPageData = ({ slug: inputSlug, isModal }: EntityPageDataProps) => {
  const router = useRouter();
  const slug = inputSlug || (router.query.slug as string);
  const source = router.query.source as string;
  const whiteBg = router.query.whiteBg as string;
  const hidePills = router.query["hide-pills"] === "true";

  const { authenticated, account, isAccountLoading } = useAuthentication();

  const { data: entity = {} } = useApi(entityUrl(slug), baseFetcher);
  const { prettyName: name, security } = entity || {};
  const shortedSecurity = shortSecurity(security);
  const pageTitleName = useMemo(() => {
    if (shortedSecurity) {
      return `${name} | ${shortedSecurity} `;
    }
    return name;
  }, [shortedSecurity, name]);
  const title = `${pageTitleName} - Analytics`;
  const description = `Historical Prices, Reported Financials, Valuation Ratios, Shareholder Registry and Sentiment Analysis on ${name} (${entity?.uniqueTicker})`;

  const { annually, isLoading } = useFinancialData(entity?.isin || "");

  const urlShareholder =
    authenticated && !account?.isPreviewPass
      ? shareholdersUrl(entity?.id, 1)
      : "";
  const { data: shareholdersData, isValidating: isShareholdersLoading } =
    useApi(urlShareholder, baseFetcher);

  const allTabs = useMemo(() => {
    const hasFinancialsData = annually.some(
      (item) =>
        !!item?.revenue ||
        !!item?.net_income ||
        !!item?.gross_income ||
        !!item?.ebitda ||
        !!item?.assets ||
        !!item?.liabilities ||
        !!item?.d2a ||
        !!item?.operating_cf ||
        !!item?.investing_cf ||
        !!item?.fin_cf ||
        !!item?.free_cf_fcfe ||
        !!item?.fcf_yield ||
        !!item?.eps_dil ||
        !!item?.div_ps ||
        !!item?.div_yield ||
        !!item?.tier1_cap ||
        !!item?.tier2_cap ||
        !!item?.loan_loss_prov ||
        !!item?.income_after_llp ||
        !!item?.deposits ||
        !!item?.premium_receivable ||
        !!item?.loss_claim_reserve
    );

    const hasProfitabilityData = annually.some(
      (item) =>
        !!item?.gross_margin ||
        !!item?.ebitda_margin ||
        !!item?.net_margin ||
        !!item?.roa ||
        !!item?.rotac ||
        !!item?.roce ||
        !!item?.dupont ||
        !!item?.asset_turnover ||
        !!item?.equity_multiplier
    );

    const hasWorkingCapitalData = annually.some(
      (item) =>
        !!item?.receivables_turnover ||
        !!item?.inventory_turnover ||
        !!item?.payables_turnover ||
        !!item?.dso ||
        !!item?.dio ||
        !!item?.dpo ||
        !!item?.cash_conversion_cycle
    );

    const hasLiquidityData =
      hasWorkingCapitalData ||
      annually.some(
        (item) =>
          !!item?.current_ratio ||
          !!item?.quick_ratio ||
          !!item?.cash_ratio ||
          !!item?.totdebt_totassets ||
          !!item?.netdebt_equity ||
          !!item?.totdebt_equity ||
          !!item?.net_debt
      );

    const hasValuationData = annually.some(
      (item) =>
        !!item?.ev_sales ||
        !!item?.ev_gp ||
        !!item?.ev_ebitda ||
        !!item?.pe ||
        !!item?.pb
    );

    const allTabs: { id: string; label: string; isNew?: boolean }[] = [
      {
        id: "snapshot",
        label: "Snapshot",
      },
      ...(hasFinancialsData
        ? [
            {
              id: "financials",
              label: "Financials",
            },
          ]
        : []),
      ...(hasProfitabilityData
        ? [
            {
              id: "profitability",
              label: "Profitability",
            },
          ]
        : []),
      ...(hasLiquidityData
        ? [
            {
              id: "liquidity",
              label: "Liquidity",
            },
          ]
        : []),
      ...(hasValuationData
        ? [
            {
              id: "valuation",
              label: "Valuation",
            },
          ]
        : []),
      ...(account?.canViewEntityConsensus
        ? [
            {
              id: "estimates",
              label: "Consensus",
            },
          ]
        : []),
      ...(authenticated &&
      !account?.isPreviewPass &&
      ((shareholdersData !== "Internal Server Error" &&
        !!shareholdersData?.data?.length) ||
        isShareholdersLoading)
        ? [
            {
              id: "shareholding",
              label: !account?.isCorporateOfficer
                ? "Shareholding"
                : "Share Registry",
            },
          ]
        : []),
      ...(hasFinancialsData && account?.canViewPeersComparison
        ? [
            {
              id: "peers",
              label: "Compare",
            },
          ]
        : []),
    ];
    return allTabs;
  }, [
    account?.canViewPeersComparison,
    account?.canViewEntityConsensus,
    account?.isCorporateOfficer,
    account?.isPreviewPass,
    annually,
    authenticated,
    isShareholdersLoading,
    shareholdersData,
  ]);

  const [activeTab, setActiveTab] = useState(allTabs[0].id);
  const currentTab = router.query.tab || activeTab || allTabs[0].id;

  useEffect(() => {
    if (!isLoading && !isShareholdersLoading && currentTab) {
      const matched = allTabs.find((tab) => tab.id === currentTab);
      !matched && !hidePills && router.push(`/entities/${slug}/analytics`);
    }
  }, [
    currentTab,
    allTabs,
    router,
    slug,
    isLoading,
    isShareholdersLoading,
    hidePills,
  ]);

  const tabContent = useMemo(() => {
    if (!entity) return null;

    if (currentTab === "snapshot") {
      return <Snapshot entity={entity} />;
    }
    if (!account?.canViewFullEntityData) {
      let tabName;
      if (currentTab === "financials") {
        tabName = "Financials";
      }
      if (currentTab === "profitability") {
        tabName = "Profitability";
      }
      if (currentTab === "liquidity") {
        tabName = "Liquidity";
      }
      if (currentTab === "working-capital") {
        tabName = "Working Capital";
      }
      if (currentTab === "valuation") {
        tabName = "Valuation";
      }
      if (currentTab === "segments") {
        tabName = "Segments";
      }
      if (currentTab === "shareholding") {
        tabName = account?.isCorporateOfficer
          ? "Share Registry"
          : "Shareholding";
      }
      if (currentTab === "esg") {
        tabName = "ESG";
      }

      if (currentTab === "peers-comparison") {
        tabName = "Peers";
      }

      if (currentTab === "estimates") {
        tabName = "Estimates";
      }

      return (
        <>
          <div className="bg-mellow-yellow rounded-xl p-[15px] text-subtle-black">
            As a {account?.accountType} holder, you do not have access to{" "}
            {tabName}. Upgrade now to get full access.
          </div>
          <div className="mt-[25px]">
            <AccountUpgradeForm />
          </div>
        </>
      );
    }
    if (currentTab === "financials") {
      return <Financials entity={entity} />;
    }
    if (currentTab === "profitability") {
      return <Profitability entity={entity} />;
    }
    if (currentTab === "liquidity") {
      return <Liquidity entity={entity} />;
    }
    if (currentTab === "working-capital") {
      return <WorkingCapital entity={entity} />;
    }
    if (currentTab === "valuation") {
      return <Valuation entity={entity} />;
    }
    if (currentTab === "segments") {
      return <Segments entity={entity} />;
    }
    if (currentTab === "shareholding") {
      return <Shareholder entity={entity} />;
    }
    if (currentTab === "esg") {
      return <ESG entity={entity} />;
    }
    if (currentTab === "peers") {
      return <PeersComparison entity={entity} />;
    }
    if (currentTab === "estimates") {
      return <EntityPageEstimatesCombined entity={entity} />;
    }
  }, [
    account?.accountType,
    account?.canViewFullEntityData,
    account?.isCorporateOfficer,
    currentTab,
    entity,
  ]);

  const { scrollableRef, onTabLoad, onTabClick, LeftNav, RightNav } =
    useScrollable();

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      {authenticated ? (
        !account && isAccountLoading ? (
          <CustomCard
            className={twMerge(
              "md:mt-[45px] p-[15px] md:p-[30px] mb-0",
              router.query?.whiteBg === "true" ? "shadow-none" : ""
            )}
          >
            <Loader />
          </CustomCard>
        ) : (
          <>
            <div
              className={twMerge(
                "relative",
                hidePills && "hidden",
                currentTab === "estimates" && "md:max-w-[calc(100%-330px)]"
              )}
            >
              <Tabs
                className={twMerge("md:overflow-x-scroll scrollbar-hide pt-0")}
                ref={scrollableRef}
              >
                <LeftNav
                  className={twMerge(
                    "h-[31]",
                    source === "native-app" && "h-[22px]"
                  )}
                />
                {allTabs.map((tab) => (
                  <Tab
                    key={tab.id}
                    href={
                      isModal
                        ? undefined
                        : {
                            pathname: router.pathname,
                            query: {
                              slug,
                              tab: tab.id,
                              ...(source ? { source } : {}),
                              ...(whiteBg ? { whiteBg } : {}),
                            },
                          }
                    }
                    onClick={() => isModal && setActiveTab(tab.id)}
                    active={currentTab === tab.id}
                    className={twMerge(
                      "md:max-w-none md:min-w-min md:overflow-hidden relative",
                      source === "native-app" &&
                        "px-[15px] py-[6px] text-[12px]",
                      tab.isNew && "p-0"
                    )}
                    ref={(ref) => onTabLoad(ref, tab.id)}
                  >
                    <div
                      className={twMerge(
                        tab.isNew &&
                          "w-[100%] h-[100%] relative overflow-hidden rounded-full px-[16px] pt-[8px] pb-[9px] pr-[25px]"
                      )}
                    >
                      <div onClick={onTabClick} className="flex items-center">
                        {tab.label}
                      </div>

                      <span
                        className={twMerge(
                          "bg-ink-pink px-[16px] py-[4px] text-[8px] ml-[5px] text-white absolute top-[2px] right-[-14px] rotate-45",
                          !tab.isNew && "hidden"
                        )}
                      >
                        New
                      </span>
                    </div>
                  </Tab>
                ))}
                <RightNav
                  className={twMerge(
                    "h-[31]",
                    source === "native-app" && "h-[22px]"
                  )}
                />
              </Tabs>
            </div>
            {currentTab === "estimates" ? (
              <div className="mt-[15px]">{tabContent}</div>
            ) : authenticated || currentTab === "snapshot" ? (
              <CustomCard
                className={twMerge(
                  "md:mt-[15px] p-[15px] md:p-[30px] mb-0 shadow-none",
                  "min-h-[max(calc(100vh-500px),720px)]"
                )}
                id="analytic-content"
              >
                <TimeScopeProvider>{tabContent}</TimeScopeProvider>
                <div className="flex justify-between items-center">
                  <div className="text-[#999] text-[12px]">
                    Source: Public Filings
                  </div>
                  <Image
                    src={logo}
                    alt="logo"
                    width={100}
                    height={20}
                    id="logo-source"
                  />
                </div>
              </CustomCard>
            ) : (
              <div className="md:mt-[45px] md:shadow">
                <SignUpForm entityId={entity?.id} type="insight-signup" />
              </div>
            )}
          </>
        )
      ) : (
        <div className="md:mt-[45px] md:shadow">
          <SignUpForm entityId={entity?.id} type="insight-signup" />
        </div>
      )}
    </>
  );
};

export default EntityPageData;
